import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { ArrowLeft, Award, Calendar, MapPin, Medal } from 'lucide-react';
import { API_BASE_URL, PANEL_URL } from '../lib/api';
import { TurnivoLogo } from '../components/TurnivoLogo';
import '../styles/landing-cinematic.css';

type PublicTournamentDetails = {
  id: string;
  name: string | null;
  status: string | null;
  format?: string | null;
  scheduleStartAtIso?: string | null;
  venue?: string | null;
  lat?: number | null;
  lng?: number | null;
  geoAddress?: string | null;
};

type PublicMatchLite = {
  id: string;
  stage?: string | null;
  status?: string | null;
  roundNumber?: number | null;
  homeTeamId?: string | null;
  awayTeamId?: string | null;
  homeSets?: number | null;
  awaySets?: number | null;
  isThirdPlaceMatch?: boolean;
};

type PodiumState = {
  first: string | null;
  second: string | null;
  third: string | null;
  source: 'PLAYOFF' | 'TABLE' | 'NONE';
};

export default function PublicTournamentDetailsPage() {
  const panelBaseUrl = useMemo(() => {
    const configured = (PANEL_URL || '').replace(/\/$/, '');
    if (import.meta.env.DEV) return configured || 'http://localhost:5173';
    try {
      const configuredHost = new URL(configured).hostname;
      const currentHost = window.location.hostname;
      // Gdy env omyłkowo wskazuje landing (turnivo.pl), wymuś domenę panelu.
      if (!configuredHost || configuredHost === currentHost) return 'https://panel.turnivo.pl';
    } catch {
      return 'https://panel.turnivo.pl';
    }
    return configured;
  }, []);

  const { id } = useParams<{ id: string }>();
  const [view, setView] = useState<'info' | 'results' | 'screens'>('info');
  const [tournament, setTournament] = useState<PublicTournamentDetails | null>(null);
  const [teams, setTeams] = useState<string[]>([]);
  const [podium, setPodium] = useState<PodiumState>({
    first: null,
    second: null,
    third: null,
    source: 'NONE',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function statusPl(status: string | null | undefined) {
    if (status === 'IN_PROGRESS') return 'W trakcie';
    if (status === 'GENERATED') return 'Zaplanowany';
    if (status === 'FINISHED') return 'Zakończony';
    if (status === 'DRAFT') return 'Szkic';
    return status ?? '—';
  }

  function formatPl(format: string | null | undefined) {
    if (format === 'ROUND_ROBIN') return 'Każdy z każdym';
    if (format === 'GROUPS_PLAYOFF') return 'Grupy + Playoff';
    if (format === 'KNOCKOUT') return 'Pucharowy (KO)';
    if (format === 'DOUBLE_ELIMINATION') return 'Podwójna eliminacja';
    return format ?? '—';
  }

  useEffect(() => {
    if (!id) return;
    let canceled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      fetch(`${API_BASE_URL}/api/public/tournaments/${id}`),
      fetch(`${API_BASE_URL}/api/public/tournaments/${id}/matches`),
      fetch(`${API_BASE_URL}/api/public/tournaments/${id}/playoff`),
      fetch(`${API_BASE_URL}/api/public/tournaments/${id}/groups/table`),
    ])
      .then(async ([tRes, mRes, pRes, gRes]) => {
        const tData = await tRes.json().catch(() => ({}));
        const mData = await mRes.json().catch(() => ({}));
        const pData = await pRes.json().catch(() => ({}));
        const gData = await gRes.json().catch(() => ({}));
        if (!tRes.ok || !tData?.ok || !tData?.tournament) {
          throw new Error(tData?.message ?? 'Błąd pobierania');
        }
        if (!canceled) {
          setTournament(tData.tournament);
          const teamsById = {
            ...((mData?.teamsById ?? {}) as Record<string, string>),
            ...((pData?.teamsById ?? {}) as Record<string, string>),
          };
          const names = Object.values(teamsById).filter(Boolean).sort((a, b) => a.localeCompare(b, 'pl'));
          setTeams(names);

          const playoffMatches = (pData?.matches ?? []) as PublicMatchLite[];
          const finished = (m?: PublicMatchLite) =>
            ['FINISHED', 'WALKOVER'].includes(String(m?.status ?? '').toUpperCase());
          const winnerName = (m?: PublicMatchLite) => {
            if (!m || !finished(m)) return null;
            const hs = Number(m.homeSets ?? 0);
            const as = Number(m.awaySets ?? 0);
            if (hs === as) return null;
            const winnerId = hs > as ? m.homeTeamId : m.awayTeamId;
            return winnerId ? teamsById[winnerId] ?? winnerId : null;
          };
          const loserName = (m?: PublicMatchLite) => {
            if (!m || !finished(m)) return null;
            const hs = Number(m.homeSets ?? 0);
            const as = Number(m.awaySets ?? 0);
            if (hs === as) return null;
            const loserId = hs > as ? m.awayTeamId : m.homeTeamId;
            return loserId ? teamsById[loserId] ?? loserId : null;
          };

          if (playoffMatches.length > 0) {
            const nonThird = playoffMatches.filter((m) => !m.isThirdPlaceMatch);
            const rounds = nonThird
              .map((m) => Number(m.roundNumber ?? 0))
              .filter((n) => Number.isFinite(n) && n > 0);
            const maxRound = rounds.length ? Math.max(...rounds) : 0;
            const finalCandidates = nonThird.filter(
              (m) =>
                Number(m.roundNumber ?? 0) === maxRound &&
                m.homeTeamId &&
                m.awayTeamId,
            );
            const finalMatch = finalCandidates.length === 1 ? finalCandidates[0] : undefined;
            const thirdMatch = playoffMatches.find((m) => m.isThirdPlaceMatch === true);

            setPodium({
              first: winnerName(finalMatch),
              second: loserName(finalMatch),
              third: winnerName(thirdMatch),
              source: 'PLAYOFF',
            });
          } else {
            const groups = (gData?.groups ?? []) as Array<{
              groupNumber: number;
              rows?: Array<{ teamId?: string; name?: string }>;
            }>;
            const firstGroup = [...groups].sort((a, b) => a.groupNumber - b.groupNumber)[0];
            const rowName = (idx: number) => {
              const row = firstGroup?.rows?.[idx];
              if (!row) return null;
              if (row.teamId && teamsById[row.teamId]) return teamsById[row.teamId];
              return row.name ?? row.teamId ?? null;
            };
            setPodium({
              first: rowName(0),
              second: rowName(1),
              third: rowName(2),
              source: firstGroup ? 'TABLE' : 'NONE',
            });
          }
        }
      })
      .catch((e: any) => {
        if (!canceled) setError(e?.message ?? 'Nie udało się pobrać turnieju.');
      })
      .finally(() => {
        if (!canceled) setLoading(false);
      });

    return () => {
      canceled = true;
    };
  }, [id]);

  const screensUrl = useMemo(() => {
    if (!id || view !== 'screens') return '';
    return `${panelBaseUrl}/public/${id}/screens`;
  }, [id, view, panelBaseUrl]);

  if (view === 'screens' && screensUrl) {
    return (
      <main className="h-screen w-screen bg-white">
        <iframe
          src={screensUrl}
          title="Publiczne ekrany turnieju"
          className="h-screen w-screen border-0"
        />
      </main>
    );
  }

  return (
    <div className="landing-cinematic motion-ready">
      <header className="header">
        <div className="nav wrap">
          <TurnivoLogo className="brand brand-portal" />
          <nav className="desktop-nav" aria-label="Nawigacja">
            <Link to="/turnieje">Turnieje</Link>
            <Link to="/">Strona główna</Link>
          </nav>
          <div className="nav-actions">
            <Link to="/turnieje" className="login" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <ArrowLeft className="w-4 h-4" /> Lista
            </Link>
            <a href={`${panelBaseUrl}/login`} className="button small">
              Otwórz panel <span aria-hidden="true">↗</span>
            </a>
          </div>
        </div>
      </header>

      <main className="section wrap" style={{ paddingTop: 48, paddingBottom: 80 }}>
        {loading ? (
          <div className="feature-card" style={{ color: 'var(--muted)' }}>
            Ładowanie turnieju…
          </div>
        ) : null}
        {error ? (
          <div
            className="feature-card"
            style={{ borderColor: '#7F1D1D', background: '#450A0A', color: '#FECACA' }}
          >
            {error}
          </div>
        ) : null}

        {!loading && !error && tournament ? (
          <>
            <div className="section-heading">
              <div>
                <div className="eyebrow">
                  <i className="status-dot" /> {statusPl(tournament.status).toUpperCase()}
                </div>
                <h1
                  style={{
                    fontSize: 'clamp(36px, 5vw, 56px)',
                    letterSpacing: '-1.5px',
                    marginTop: 12,
                    lineHeight: 1.08,
                  }}
                >
                  {tournament.name ?? 'Turniej'}
                </h1>
              </div>
              <p>
                Format: <strong style={{ color: 'var(--text)' }}>{formatPl(tournament.format)}</strong>
              </p>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 28 }}>
              <button
                type="button"
                onClick={() => setView('info')}
                className={view === 'info' ? 'button small' : 'button outline small'}
                style={{ padding: '12px 16px', fontSize: 13, gap: 10 }}
              >
                Informacje
              </button>
              <button
                type="button"
                onClick={() => setView('results')}
                className={view === 'results' ? 'button small' : 'button outline small'}
                style={{
                  padding: '12px 16px',
                  fontSize: 13,
                  gap: 10,
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                <Award className="w-4 h-4" /> Wyniki
              </button>
              <button
                type="button"
                onClick={() => setView('screens')}
                className={view === 'screens' ? 'button small' : 'button outline small'}
                style={{ padding: '12px 16px', fontSize: 13, gap: 10 }}
              >
                Ekrany
              </button>
            </div>

            {view === 'info' ? (
              <div className="feature-card">
                <div className="eyebrow" style={{ marginBottom: 16 }}>
                  DANE TURNIEJU
                </div>
                <div style={{ display: 'grid', gap: 12, fontSize: 14, color: 'var(--muted)' }}>
                  <div>
                    Nazwa:{' '}
                    <strong style={{ color: 'var(--text)' }}>{tournament.name ?? '—'}</strong>
                  </div>
                  <div>
                    Status:{' '}
                    <strong style={{ color: 'var(--text)' }}>{statusPl(tournament.status)}</strong>
                  </div>
                  <div>
                    Format:{' '}
                    <strong style={{ color: 'var(--text)' }}>{formatPl(tournament.format)}</strong>
                  </div>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <Calendar className="w-4 h-4" color="#00C7B1" />
                    Start:{' '}
                    <strong style={{ color: 'var(--text)' }}>
                      {tournament.scheduleStartAtIso
                        ? new Date(tournament.scheduleStartAtIso).toLocaleString('pl-PL', {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })
                        : '—'}
                    </strong>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
                    <span>
                      Miejsce:{' '}
                      <strong style={{ color: 'var(--text)' }}>{tournament.venue ?? '—'}</strong>
                    </span>
                    {typeof tournament.lat === 'number' && typeof tournament.lng === 'number' ? (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${tournament.lat},${tournament.lng}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-link"
                        style={{ marginTop: 0, paddingBottom: 2, gap: 6, fontSize: 13 }}
                      >
                        <MapPin className="w-4 h-4" /> Mapa
                      </a>
                    ) : null}
                  </div>
                  {tournament.geoAddress ? <div>{tournament.geoAddress}</div> : null}
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
                      Lista drużyn
                    </div>
                    {teams.length ? (
                      <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6 }}>
                        {teams.map((team) => (
                          <li key={team}>{team}</li>
                        ))}
                      </ul>
                    ) : (
                      <div>Brak drużyn.</div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="feature-card">
                <div className="eyebrow" style={{ marginBottom: 12 }}>
                  WYNIKI KOŃCOWE
                </div>
                <h2 style={{ fontSize: 28, marginBottom: 8 }}>Podium turnieju</h2>
                <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 22 }}>
                  {podium.source === 'PLAYOFF'
                    ? 'Podium wyliczone na podstawie fazy pucharowej.'
                    : podium.source === 'TABLE'
                      ? 'Podium wyliczone na podstawie tabeli turnieju.'
                      : 'Wyniki pojawią się po rozegraniu decydujących meczów.'}
                </p>
                <div className="feature-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                  <div className="feature-card" style={{ borderColor: '#00C7B155' }}>
                    <div className="eyebrow" style={{ marginBottom: 10 }}>
                      <Medal className="w-4 h-4" /> 1. MIEJSCE
                    </div>
                    <h3 style={{ margin: 0 }}>{podium.first ?? '—'}</h3>
                  </div>
                  <div className="feature-card">
                    <div className="eyebrow" style={{ marginBottom: 10 }}>
                      <Medal className="w-4 h-4" /> 2. MIEJSCE
                    </div>
                    <h3 style={{ margin: 0 }}>{podium.second ?? '—'}</h3>
                  </div>
                  <div className="feature-card">
                    <div className="eyebrow" style={{ marginBottom: 10 }}>
                      <Medal className="w-4 h-4" /> 3. MIEJSCE
                    </div>
                    <h3 style={{ margin: 0 }}>{podium.third ?? '—'}</h3>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : null}
      </main>
    </div>
  );
}
